import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Tabs, Modal, Form, Input, Select, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const TaskList = () => {
  const [activeTab, setActiveTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [hostels, setHostels] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const { user } = useAuth();
  const [taskForm] = Form.useForm();
  const [requestForm] = Form.useForm();

  const isAssignedToMe = (task) => {
    const currentUserId = user?._id ?? user?.id;
    if (!task.assignedTo || !currentUserId) return false;
    const assignedId = task.assignedTo._id ? String(task.assignedTo._id) : String(task.assignedTo);
    return assignedId === String(currentUserId);
  };

  const canCreateTask = user?.role === 'owner' || (user?.role === 'employee' && user?.employeeType === 'accountant');
  const canCreateRequest = user?.role === 'employee' && ['cook', 'driver', 'cleaner'].includes(user?.employeeType);
  const canManageRequests = user?.role === 'owner' || (user?.role === 'employee' && user?.employeeType === 'accountant');

  useEffect(() => {
    if (activeTab === 'tasks') fetchTasks();
    else fetchRequests();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'tasks' && showTaskModal) {
      Promise.all([api.get('/hostels'), api.get('/admin/team-members')]).then(([hRes, tRes]) => {
        setHostels(hRes.data);
        setTeamMembers(tRes.data);
      }).catch(() => {});
    }
    if (activeTab === 'requests' && showRequestModal) {
      api.get('/hostels').then((res) => setHostels(res.data)).catch(() => {});
    }
  }, [activeTab, showTaskModal, showRequestModal]);

  const taskHostelId = Form.useWatch('hostel', taskForm);
  useEffect(() => {
    if (!taskHostelId || !teamMembers.length) {
      setFilteredEmployees([]);
      return;
    }
    const filtered = teamMembers.filter((emp) => {
      if (!emp) return false;
      const empHostel = emp.hostel?._id || emp.hostel;
      const empHostels = emp.accessibleHostels || [];
      const hid = taskHostelId;
      return (empHostel && String(empHostel) === hid) || empHostels.some((h) => (h._id || h).toString() === hid);
    });
    setFilteredEmployees(filtered);
    taskForm.setFieldValue('assignedTo', undefined);
  }, [taskHostelId, teamMembers, taskForm]);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/housekeeping');
      setTasks(response.data);
    } catch (error) {
      message.error('Failed to load tasks');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await api.get('/employee-requests');
      setRequests(response.data);
    } catch (error) {
      message.error('Failed to load requests');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskStatusChange = async (taskId, status) => {
    try {
      if (status === 'completed') {
        Modal.confirm({
          title: 'Mark task as done?',
          content: 'Are you sure you want to mark this task as completed?',
          okText: 'Yes',
          onOk: async () => {
            await api.patch(`/housekeeping/${taskId}/status`, { status });
            message.success('Task completed');
            fetchTasks();
          },
        });
      } else {
        await api.patch(`/housekeeping/${taskId}/status`, { status });
        message.success('Status updated');
        fetchTasks();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Update failed');
    }
  };

  const handleRequestStatusChange = async (requestId, status) => {
    try {
      await api.patch(`/employee-requests/${requestId}/status`, { status });
      message.success('Request updated');
      fetchRequests();
    } catch (error) {
      message.error(error.response?.data?.message || 'Update failed');
    }
  };

  const handleAddTask = async () => {
    try {
      const values = await taskForm.validateFields();
      await api.post('/housekeeping', {
        hostel: values.hostel,
        title: values.title,
        description: values.description,
        assignedTo: values.assignedTo,
      });
      message.success('Task created');
      taskForm.resetFields();
      setShowTaskModal(false);
      fetchTasks();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed to create task');
    }
  };

  const handleAddRequest = async () => {
    try {
      const values = await requestForm.validateFields();
      await api.post('/employee-requests', {
        hostel: values.hostel,
        requestType: values.requestType,
        title: values.title,
        description: values.description,
      });
      message.success('Request submitted');
      requestForm.resetFields();
      setShowRequestModal(false);
      fetchRequests();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed to submit request');
    }
  };

  const taskColumns = [
    { title: 'Title', dataIndex: 'title', key: 'title', render: (t) => <strong>{t}</strong> },
    { title: 'Hostel', dataIndex: ['hostel', 'name'], key: 'hostel', render: (t) => t || 'N/A' },
    { title: 'Assigned To', dataIndex: ['assignedTo', 'name'], key: 'assignedTo', render: (t) => t || 'Unassigned' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={s === 'completed' ? 'green' : s === 'in_progress' ? 'blue' : 'default'}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) =>
        record.status !== 'completed' && isAssignedToMe(record) ? (
          <Button type="primary" size="small" onClick={() => handleTaskStatusChange(record._id, 'completed')}>Mark Done</Button>
        ) : null,
    },
  ];

  const requestColumns = [
    { title: 'Title', dataIndex: 'title', key: 'title', render: (t) => <strong>{t}</strong> },
    { title: 'Type', dataIndex: 'requestType', key: 'type', render: (t) => <Tag>{t?.replace('_', ' ')}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'desc', render: (t) => t || 'N/A' },
    { title: 'Hostel', dataIndex: ['hostel', 'name'], key: 'hostel', render: (t) => t || 'N/A' },
    { title: 'Requested By', dataIndex: ['requestedBy', 'name'], key: 'requestedBy', render: (t) => t || 'N/A' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={s === 'approved' ? 'green' : s === 'rejected' ? 'red' : 'orange'}>{s}</Tag>,
    },
    ...(canManageRequests
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) =>
              record.status === 'pending' ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="primary" size="small" onClick={() => handleRequestStatusChange(record._id, 'approved')}>Approve</Button>
                  <Button danger size="small" onClick={() => handleRequestStatusChange(record._id, 'rejected')}>Reject</Button>
                </div>
              ) : null,
          },
        ]
      : []),
  ];

  const tabItems = [
    {
      key: 'tasks',
      label: 'Tasks',
      children: (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            {canCreateTask && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowTaskModal(true)}>Add Task</Button>
            )}
          </div>
          <Table rowKey="_id" columns={taskColumns} dataSource={tasks} loading={loading} pagination={{ pageSize: 10 }} locale={{ emptyText: 'No tasks found' }} scroll={{ x: 'max-content' }} />
        </>
      ),
    },
    {
      key: 'requests',
      label: 'Requests',
      children: (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            {canCreateRequest && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowRequestModal(true)}>Add Request</Button>
            )}
          </div>
          <Table rowKey="_id" columns={requestColumns} dataSource={requests} loading={loading} pagination={{ pageSize: 10 }} locale={{ emptyText: 'No requests found' }} scroll={{ x: 'max-content' }} />
        </>
      ),
    },
  ];

  return (
    <>
      <Card title={<span style={{ fontSize: 20, fontWeight: 600 }}>Tasks & Requests</span>}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      <Modal title="Add Task" open={showTaskModal} onCancel={() => { setShowTaskModal(false); taskForm.resetFields(); }} onOk={handleAddTask} destroyOnClose okText="Create">
        <Form form={taskForm} layout="vertical">
          <Form.Item name="hostel" label="Hostel" rules={[{ required: true }]}>
            <Select placeholder="Select hostel" options={hostels.map((h) => ({ value: h._id, label: h.name }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Task title" />
          </Form.Item>
          <Form.Item name="assignedTo" label="Assign To" rules={[{ required: true }]}>
            <Select placeholder="Select employee" disabled={!taskHostelId} options={filteredEmployees.map((e) => ({ value: e._id, label: `${e.name} (${e.employeeType})` }))} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Description" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add Request" open={showRequestModal} onCancel={() => { setShowRequestModal(false); requestForm.resetFields(); }} onOk={handleAddRequest} destroyOnClose okText="Submit Request">
        <Form form={requestForm} layout="vertical">
          <Form.Item name="hostel" label="Hostel" rules={[{ required: true }]}>
            <Select placeholder="Select hostel" options={hostels.map((h) => ({ value: h._id, label: h.name }))} />
          </Form.Item>
          <Form.Item name="requestType" label="Request Type" initialValue="food" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'food', label: 'Food' },
                { value: 'store', label: 'Store Items' },
                { value: 'vehicle_fuel', label: 'Vehicle Fuel' },
                { value: 'vehicle_repair', label: 'Vehicle Repair' },
                { value: 'cleaning_supplies', label: 'Cleaning Supplies' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Description" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TaskList;
